// src/features/intentions/context/IntentionsContext.jsx
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from '../../auth/context/AuthContext';
import {
  subscribeToWeekIntentions,
  addIntention,
  updateIntentionStatus,
  carryForwardIntention,
  getCurrentWeekId,
  getNextWeekId,
} from '../services/intentionsService';

const IntentionsContext = createContext(null);

export function IntentionsProvider({ children }) {
  const { user } = useAuth();
  const [intentions, setIntentions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Recompute weekId at mount and refresh every minute so it stays current across
  // week boundaries (e.g. user leaves the app open over midnight Sunday).
  const [currentWeekId, setCurrentWeekId] = useState(getCurrentWeekId);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWeekId(getCurrentWeekId());
    }, 60_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      setIntentions([]);
      return;
    }
    setLoading(true);
    setError(null);
    const unsub = subscribeToWeekIntentions(
      user.uid,
      currentWeekId,
      (data) => { setIntentions(data); setLoading(false); },
      (err) => { console.error('[IntentionsContext] subscription error:', err); setError(err); setLoading(false); },
    );
    return unsub;
  }, [user?.uid, currentWeekId]);

  async function add(text, opts = {}) {
    if (!user || !text?.trim()) return;
    await addIntention(user.uid, text, currentWeekId, opts);
  }

  async function markDone(intentionId) {
    try {
      await updateIntentionStatus(intentionId, 'done');
    } catch (err) {
      console.error('[IntentionsContext] markDone failed:', err);
      throw err;
    }
  }

  async function release(intentionId) {
    try {
      await updateIntentionStatus(intentionId, 'released');
    } catch (err) {
      console.error('[IntentionsContext] release failed:', err);
      throw err;
    }
  }

  async function carry(intention) {
    // Current-week intentions carry forward to next week.
    // Prior-week pending intentions carry into the current week.
    const targetWeekId = intention.weekId === currentWeekId ? getNextWeekId() : currentWeekId;
    try {
      await carryForwardIntention(intention, targetWeekId);
    } catch (err) {
      console.error('[IntentionsContext] carry failed:', err);
      throw err;
    }
  }

  const active = intentions.filter(i => i.status === 'pending');
  const done = intentions.filter(i => i.status === 'done');
  const released = intentions.filter(i => i.status === 'released');

  return (
    <IntentionsContext.Provider value={{
      intentions, active, done, released,
      loading, error, currentWeekId,
      add, markDone, release, carry,
    }}>
      {children}
    </IntentionsContext.Provider>
  );
}

export function useIntentions() {
  const ctx = useContext(IntentionsContext);
  if (!ctx) throw new Error('useIntentions must be used within IntentionsProvider');
  return ctx;
}
